<template>
  <div
    class="a-pricing-strategy-recommendation-tooltip d-flex flex-column black--text"
    :style="`gap:10px; --side-margin: ${sideMargin}%`"
  >
    <!-- Primera línea con datos comunes -->
    <v-row no-gutters style="gap: 5px">
      <v-col
        cols="3"
        class="d-flex align-center justify-center grey lighten-3 pa-1 rounded"
        style="gap: 5px; flex: 1"
      >
        <v-icon size="12">mdi-bell-check-outline</v-icon>
        <div class="text-caption font-weight-bold">{{ commonTime }}</div>
      </v-col>
      <v-col
        cols="3"
        class="d-flex align-center justify-center grey lighten-3 pa-1 rounded"
        style="gap: 5px; flex: 1"
      >
        <v-icon size="12">mdi-calendar-outline</v-icon>
        <div class="text-caption font-weight-bold">
          {{ suggestion.anticipation }} {{ $t("days") }}
        </div>
      </v-col>
      <v-col
        cols="3"
        class="d-flex align-center justify-center grey lighten-3 pa-1 rounded"
        style="gap: 5px; flex: 1"
      >
        <v-icon size="12">mdi-notebook-outline</v-icon>
        <div class="text-caption font-weight-bold text-uppercase">
          {{ commonRateCode }}
        </div>
      </v-col>
      <v-col
        cols="3"
        class="d-flex align-center justify-center grey lighten-3 pa-1 rounded"
        style="gap: 5px; flex: 1"
      >
        <v-icon size="12">mdi-bed-outline</v-icon>
        <div class="text-caption font-weight-bold">{{ commonRoomType }}</div>
      </v-col>
    </v-row>

    <!-- Barra principal de recomendación -->
    <div class="suggestion-row d-flex align-end">
      <div
        class="suggestion-label d-flex align-center justify-space-between"
        style="margin-bottom: 7px;"
      >
        <template v-if="hasMultipleSuggestions">
          <div class="d-flex align-center" style="gap: 5px">
            <v-icon size="12">mdi-lightbulb-on-outline</v-icon>
            <div class="text-caption font-weight-bold">
              {{ $t("Recommendation") }}
            </div>
          </div>
        </template>
        <template v-else>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                class="d-flex align-center clickable-rule-info"
                style="gap: 5px; cursor: pointer; z-index: 100; position: relative;"
                @click.stop.prevent="showRuleInfoDialog(pricedSuggestions[0])"
              >
                <div
                  class="text-caption font-weight-bold d-flex align-center chip"
                  :style="
                    `background-color: ${getRuleColor(
                      pricedSuggestions[0].rule_id
                    )}`
                  "
                >
                  <v-icon
                    size="14"
                    :color="getRuleIconColor(pricedSuggestions[0].rule_id)"
                  >
                    {{ getSuggestionIcon(pricedSuggestions[0]) }}
                  </v-icon>
                </div>
                <div class="d-flex flex-column" style="gap: 2px">
                  <div class="text-caption font-weight-bold">
                    {{ getSuggestionMeasure(pricedSuggestions[0]) }}
                  </div>
                  <div
                    class="d-flex align-center text-caption"
                    style="gap: 5px"
                  >
                    <span
                      :style="
                        `color: ${intensifyColor(
                          getStrategyColor(pricedSuggestions[0].strategy_id)
                        )}`
                      "
                    >
                      {{ getSuggestionStrategyName(pricedSuggestions[0]) }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
            <span>{{ $t("View_rule") }}</span>
          </v-tooltip>
        </template>
        <div
          class="font-weight-bold text-caption"
          :class="totalDifferenceClass"
        >
          {{ totalFormattedDifference }}€
        </div>
      </div>
      <div class="suggestion-bar-container">
        <!-- Overlay para bloquear interacciones cuando hay hover en tooltip -->
        <div
          v-if="hoveredTooltip"
          class="tooltip-overlay"
          :style="{ zIndex: 40 }"
        ></div>

        <!-- Tooltips fijos para la primera barra -->
        <div class="fixed-tooltips">
          <div
            class="fixed-tooltip current-tooltip"
            :style="currentTooltipStyleWithZIndex"
            @mouseenter="onTooltipMouseEnter('current')"
            @mouseleave="onTooltipMouseLeave"
          >
            <div class="tooltip-content">
              {{ $t("Actual") }}:
              {{ suggestionCurrentFormattedPrice }}
            </div>
            <div class="tooltip-arrow"></div>
          </div>
          <div
            class="fixed-tooltip suggestion-tooltip"
            :style="suggestionTooltipStyleWithZIndex"
            @mouseenter="onTooltipMouseEnter('suggestion')"
            @mouseleave="onTooltipMouseLeave"
          >
            <div class="tooltip-content">
              {{ $t("Recommended") }}:
              {{ suggestionRecommendedFormattedPrice }}
            </div>
            <div class="tooltip-arrow"></div>
          </div>
        </div>

        <!-- Contenedor de barra con ancho fijo -->
        <div class="bar-container" :style="barContainerStyle">
          <!-- Barra visual -->
          <div class="bar-visual with-margins">
            <!-- Barra base gris de ancho completo -->
            <div class="bar-base"></div>

            <!-- Barra de color proporcional desde price_initial -->
            <div
              class="bar-color"
              :class="totalDifferenceClass"
              :style="totalColorBarStyle"
            ></div>

            <!-- Puntos de precio -->
            <div
              class="current-price-point current-point"
              :style="currentPricePointStyle"
            ></div>
            <div
              class="current-price-point suggestion-point"
              :style="suggestionPricePointStyle"
            ></div>
          </div>

          <!-- Precios debajo -->
          <div class="price-labels">
            <div
              v-if="minPrice !== suggestion.price_initial"
              class="price-label min-price"
              :style="minPriceStyle"
            >
              {{ minPriceFormatted }}
            </div>
            <div class="price-label start-price" :style="startPriceStyle">
              {{ suggestionInitialFormattedPrice }}
            </div>
            <div
              v-if="maxPrice !== suggestion.price_initial"
              class="price-label max-price"
              :style="maxPriceStyle"
            >
              {{ maxPriceFormatted }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Barras individuales para cada sugerencia (solo si hay más de una) -->
    <div
      v-if="hasMultipleSuggestions"
      class="d-flex flex-column mt-3"
      style="gap: 10px;"
    >
      <div
        v-for="(sug, index) in pricedSuggestions"
        :key="index"
        class="suggestion-row d-flex align-center"
        :class="{ discarded: !contributesToPrice(sug) }"
      >
        <div class="suggestion-label d-flex align-center justify-space-between">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                class="d-flex align-center clickable-rule-info"
                style="gap: 5px; cursor: pointer; z-index: 100; position: relative;"
                @click.stop.prevent="showRuleInfoDialog(sug)"
              >
                <div
                  class="text-caption font-weight-bold d-flex align-center chip"
                  :style="`background-color: ${getRuleColor(sug.rule_id)}`"
                >
                  <v-icon size="14" :color="getRuleIconColor(sug.rule_id)">
                    {{ getSuggestionIcon(sug) }}
                  </v-icon>
                </div>
                <div class="d-flex flex-column" style="gap: 2px">
                  <div class="text-caption font-weight-bold">
                    {{ getSuggestionMeasure(sug) }}
                  </div>
                  <div
                    class="d-flex align-center text-caption"
                    style="gap: 5px"
                  >
                    <span
                      :style="
                        `color: ${intensifyColor(
                          getStrategyColor(sug.strategy_id)
                        )}`
                      "
                    >
                      {{ getSuggestionStrategyName(sug) }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
            <span>{{ $t("View_rule") }}</span>
          </v-tooltip>
          <v-tooltip bottom :disabled="contributesToPrice(sug)">
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                class="font-weight-bold text-caption"
                :class="getIndividualDifferenceClass(sug)"
              >
                {{ getIndividualFormattedDifference(sug) }}€
              </div>
            </template>
            <span>{{ discardedReason(sug) }}</span>
          </v-tooltip>
        </div>
        <div class="suggestion-bar-container">
          <!-- Contenedor de barra con ancho fijo -->
          <div class="bar-container">
            <!-- Barra visual con márgenes -->
            <div class="bar-visual individual-bar">
              <!-- Barra base gris de ancho completo -->
              <div class="bar-base"></div>

              <!-- Barra de color proporcional desde price_initial -->
              <div
                class="bar-color"
                :class="getIndividualDifferenceClass(sug)"
                :style="getIndividualColorBarStyle(sug)"
              ></div>

              <!-- Sin puntos en las barras individuales -->
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Rule Info dialog -->
    <a-pricing-strategy-rule-definition-info-dialog />
  </div>
</template>

<script>
import aPricingStrategyRuleDefinitionInfoDialog from "@/components/pricing-strategy/aPricingStrategyRuleDefinitionInfoDialog";
import { measurePhrase } from "@/components/pricing-strategy/pricingStrategyUtils";
import * as Utils from "@/utils.js";
import Vue from "vue";
import { mapGetters } from "vuex";
var numeral = require("numeral");

export default {
  name: "a-pricing-strategy-recommendation-tooltip",
  components: {
    aPricingStrategyRuleDefinitionInfoDialog
  },
  props: {
    suggestion: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    // Margen lateral para las barras (porcentaje)
    sideMargin: 20,
    // Control de hover para tooltips
    hoveredTooltip: null
  }),
  computed: {
    rule_definition_info_dialog: {
      get() {
        return this.$store.state.pricingstrategy.rule_definition_info_dialog;
      },
      set(value) {
        this.$store.dispatch(
          "setPricingStrategyRuleDefinitionInfoDialog",
          value
        );
      }
    },
    rule_info: {
      get() {
        return this.$store.state.pricingstrategy.rule_info;
      },
      set(value) {
        this.$store.dispatch("setPricingStrategyRuleInfo", value);
      }
    },
    // Área útil de la barra (descontando márgenes laterales)
    usableBarWidth() {
      return 100 - this.sideMargin * 2;
    },
    // Los avisos no viven aquí: tienen su propio icono en la columna de la
    // fecha, junto a los comentarios y los eventos. Este tooltip explica el
    // precio, y un aviso no lo mueve.
    pricedSuggestions() {
      return (this.suggestion.suggestions || []).filter(
        sug => sug.suggestion_type !== "notify"
      );
    },
    // Cuál de las sugerencias decide el precio final.
    //
    // Reproduce la precedencia de StrategyApi::setPricingSuggestions(): manda
    // la ÚLTIMA tarifa fija y, si no hay ninguna, la suma de los ajustes. Es la
    // misma deducción que hace la tabla de Recomendaciones.
    priceDecider() {
      const fixed = this.pricedSuggestions.filter(
        sug => sug.suggestion_type === "fixed_price"
      );
      return fixed.length ? fixed[fixed.length - 1] : null;
    },
    hasMultipleSuggestions() {
      return this.pricedSuggestions.length > 1;
    },
    // Datos comunes de la primera línea
    commonTime() {
      return Vue.moment(this.suggestion.time, "HH:mm:ss").format("HH:mm");
    },
    commonRateCode() {
      if (this.suggestion && this.suggestion.rate_plan_id) {
        const ratePlan = this.getPricingStrategyStrategyRatePlanById(
          this.suggestion.rate_plan_id
        );
        return (ratePlan && ratePlan.code) || "N/A";
      }
      return "BAR";
    },
    commonRoomType() {
      if (this.suggestion && this.suggestion.room_type_id) {
        const roomType = this.getPricingStrategyStrategyRoomTypeById(
          this.suggestion.room_type_id
        );
        return (roomType && roomType.name) || "N/A";
      }
      return "BAR";
    },
    // Cálculos de precios para las barras
    // Sólo los precios propuestos de verdad: un aviso trae `price_suggestion`
    // a null y colaba un 0 en la escala, que dejaba el mínimo de la barra en
    // "0,00€" y descuadraba todas las posiciones.
    allSuggestionPrices() {
      if (!this.pricedSuggestions.length)
        return [this.suggestion.price_suggestion];
      return this.pricedSuggestions
        .map(sug => sug.price_suggestion)
        .filter(price => price !== null && price !== undefined);
    },
    minPrice() {
      // Incluir price_initial, price_current y todas las sugerencias para tener un rango adecuado
      const allPrices = [
        this.suggestion.price_initial,
        this.suggestion.price_current,
        this.suggestion.price_suggestion,
        ...this.allSuggestionPrices
      ];
      return Math.min(...allPrices);
    },
    maxPrice() {
      // Incluir price_initial, price_current y todas las sugerencias para tener un rango adecuado
      const allPrices = [
        this.suggestion.price_initial,
        this.suggestion.price_current,
        this.suggestion.price_suggestion,
        ...this.allSuggestionPrices
      ];
      return Math.max(...allPrices);
    },
    // Calculo de la diferencia total
    totalDifference() {
      return this.suggestion.suggestion;
    },
    totalFormattedDifference() {
      // Una recomendación formada sólo por avisos no mueve el precio y no
      // tiene diferencia que enseñar.
      if (this.totalDifference === null || this.totalDifference === undefined) {
        return "";
      }
      const diff = this.totalDifference.toFixed(0);
      return diff >= 0 ? `+${diff}` : diff;
    },
    totalDifferenceClass() {
      return this.totalDifference >= 0 ? "positive" : "negative";
    },
    // Precios formateados
    suggestionCurrentFormattedPrice() {
      return (
        numeral(this.suggestion.price_current).format(this.l.number1_format) +
        this.l.money_post_symbol
      );
    },
    suggestionRecommendedFormattedPrice() {
      return (
        numeral(this.suggestion.price_suggestion).format(
          this.l.number1_format
        ) + this.l.money_post_symbol
      );
    },
    suggestionInitialFormattedPrice() {
      return (
        numeral(this.suggestion.price_initial).format(this.l.number1_format) +
        this.l.money_post_symbol
      );
    },
    minPriceFormatted() {
      return (
        numeral(this.minPrice).format(this.l.number1_format) +
        this.l.money_post_symbol
      );
    },
    maxPriceFormatted() {
      return (
        numeral(this.maxPrice).format(this.l.number1_format) +
        this.l.money_post_symbol
      );
    },
    // Posición de los puntos de precio
    currentPricePointStyle() {
      return this.createPositionStyle(this.suggestion.price_current);
    },
    suggestionPricePointStyle() {
      return this.createPositionStyle(this.suggestion.price_suggestion);
    },
    // Estilos para tooltips posicionados
    currentTooltipStyle() {
      return this.createPositionStyle(
        this.suggestion.price_current,
        "translateX(-49%)"
      );
    },
    suggestionTooltipStyle() {
      return this.createPositionStyle(
        this.suggestion.price_suggestion,
        "translateX(-49%)"
      );
    },
    // Estilos dinámicos con z-index para hover
    currentTooltipStyleWithZIndex() {
      const baseStyle = this.currentTooltipStyle;
      return {
        ...baseStyle,
        zIndex: this.hoveredTooltip === "current" ? 50 : 30,
        pointerEvents: "auto"
      };
    },
    suggestionTooltipStyleWithZIndex() {
      const baseStyle = this.suggestionTooltipStyle;
      return {
        ...baseStyle,
        zIndex: this.hoveredTooltip === "suggestion" ? 50 : 30,
        pointerEvents: "auto"
      };
    },
    // Estilo para el contenedor de barra con pointer-events condicionales
    barContainerStyle() {
      return {
        pointerEvents: this.hoveredTooltip ? "none" : "auto"
      };
    },
    // Estilos para labels de precios posicionados
    minPriceStyle() {
      return {
        left: `${this.sideMargin}%`, // Empieza en el margen izquierdo
        transform: "translateX(-100%)"
      };
    },
    startPriceStyle() {
      return this.createPositionStyle(
        this.suggestion.price_initial,
        "translateX(-50%)"
      );
    },
    maxPriceStyle() {
      return {
        left: `${100 - this.sideMargin}%`, // Termina en el margen derecho
        transform: "translateX(0)"
      };
    },
    // Estilos para la barra de color total
    totalColorBarStyle() {
      if (this.maxPrice === this.minPrice) return { width: "0%", left: "0%" };

      const startPosition = this.calculateAdjustedPosition(
        this.suggestion.price_initial
      );
      const suggestionPosition = this.calculateAdjustedPosition(
        this.suggestion.price_suggestion
      );

      if (this.totalDifference >= 0) {
        // Positivo: verde hacia la derecha desde price_initial
        const width = suggestionPosition - startPosition;
        return {
          left: `${startPosition}%`,
          width: `${Math.max(0, width)}%`
        };
      } else {
        // Negativo: rojo hacia la izquierda desde price_initial
        const width = startPosition - suggestionPosition;
        return {
          left: `${suggestionPosition}%`,
          width: `${Math.max(0, width)}%`
        };
      }
    },
    ...mapGetters({
      current_hotel: "current_hotel",
      getPricingStrategyStrategyRatePlanById:
        "getPricingStrategyStrategyRatePlanById",
      getPricingStrategyStrategyRoomTypeById:
        "getPricingStrategyStrategyRoomTypeById",
      getPricingStrategyRuleById: "getPricingStrategyRuleById",
      getPricingStrategyRuleTypeByCode: "getPricingStrategyRuleTypeByCode",
      getPricingStrategyStrategyById: "getPricingStrategyStrategyById",
      l: "current_hotel_language"
    })
  },

  methods: {
    // Función helper para calcular posición ajustada con márgenes
    calculateAdjustedPosition(price) {
      const range = this.maxPrice - this.minPrice;
      if (range === 0) return 50; // Centro si no hay rango

      const position = ((price - this.minPrice) / range) * 100;
      // Ajustar para los márgenes: posición real = margen + (área_útil * posición)
      return this.sideMargin + (position * this.usableBarWidth) / 100;
    },
    // Función helper para crear estilo de posición con límites
    createPositionStyle(price, transform = null) {
      const position = this.calculateAdjustedPosition(price);
      const limitedPosition = Math.max(
        this.sideMargin,
        Math.min(100 - this.sideMargin, position)
      );

      const style = { left: `${limitedPosition}%` };
      if (transform) {
        style.transform = transform;
      }
      return style;
    },
    // Frases y datos de sugerencia centralizados en pricingStrategyUtils.js
    getSuggestionMeasure(sug) {
      // Por qué ha saltado la sugerencia (medida tomada); si la sugerencia
      // no trae datos de medida, fallback al nombre de la regla
      const rule = this.getPricingStrategyRuleById(sug.rule_id);
      return measurePhrase(this, sug) || (rule && rule.name) || "";
    },
    getSuggestionIcon(sug) {
      const ruleType = this.getPricingStrategyRuleTypeByCode(
        sug.rule_type_code
      );
      return (ruleType && ruleType.icon) || "mdi-lightbulb-on-outline";
    },
    getSuggestionStrategyName(sug) {
      if (sug.strategy_name) return sug.strategy_name;
      const strategy = this.getPricingStrategyStrategyById(sug.strategy_id);
      return strategy && strategy.name ? strategy.name : "N/A";
    },
    intensifyColor(color) {
      return Utils.intensifyColor(color);
    },
    getRuleColor(rule_id) {
      const rule = this.getPricingStrategyRuleById(rule_id);
      // Fallback hex: intensifyColor asume formato #RRGGBB
      return (rule && rule.color) || "#e0e0e0";
    },
    getRuleIconColor(rule_id) {
      return Utils.intensifyColor(this.getRuleColor(rule_id));
    },
    getStrategyColor(strategy_id) {
      const strategy = this.getPricingStrategyStrategyById(strategy_id);
      return strategy && strategy.color ? strategy.color : "grey";
    },
    showRuleInfoDialog(suggestion) {
      this.rule_info = suggestion;
      this.rule_definition_info_dialog = true;
    },
    // Métodos para manejar hover de tooltips
    onTooltipMouseEnter(tooltipType) {
      this.hoveredTooltip = tooltipType;
    },
    onTooltipMouseLeave() {
      this.hoveredTooltip = null;
    },
    // Métodos para las barras individuales
    // Cuánto mueve el precio esta sugerencia.
    //
    // NO es `suggestion.suggestion`: ese campo guarda el valor tal cual lo
    // emitió la regla, que en un ajuste es el movimiento (+10) pero en una
    // tarifa fija es el PRECIO (193). Enseñarlo tal cual pintaba "+193€" donde
    // el efecto real era -16€, y de rebote dejaba la barra sin ancho porque la
    // creía positiva. El movimiento es siempre la distancia al precio inicial.
    getIndividualDifference(suggestionItem) {
      const price = suggestionItem.price_suggestion;
      if (price === null || price === undefined) return null;
      return price - this.suggestion.price_initial;
    },
    getIndividualFormattedDifference(suggestionItem) {
      const value = this.getIndividualDifference(suggestionItem);
      if (value === null || value === undefined) return "";
      const diff = value.toFixed(0);
      return diff >= 0 ? `+${diff}` : diff;
    },
    // Una tarifa fija descarta los ajustes en lugar de sumarse a ellos, así que
    // hay reglas que saltan y no tocan el precio final. Se ven, pero apagadas.
    contributesToPrice(suggestionItem) {
      const decider = this.priceDecider;
      if (!decider) return true;
      return suggestionItem === decider;
    },
    discardedReason(suggestionItem) {
      if (this.contributesToPrice(suggestionItem)) return "";
      return this.$t("suggestion_overridden", {
        rule: this.getSuggestionStrategyName(this.priceDecider)
      });
    },
    getIndividualDifferenceClass(suggestionItem) {
      return this.getIndividualDifference(suggestionItem) >= 0
        ? "positive"
        : "negative";
    },
    getIndividualColorBarStyle(suggestionItem) {
      if (this.maxPrice === this.minPrice) return { width: "0%", left: "0%" };

      const startPosition = this.calculateAdjustedPosition(
        this.suggestion.price_initial
      );
      const suggestionPosition = this.calculateAdjustedPosition(
        suggestionItem.price_suggestion
      );
      const difference = this.getIndividualDifference(suggestionItem);

      if (difference >= 0) {
        // Positivo: verde hacia la derecha desde price_initial
        const width = suggestionPosition - startPosition;
        return {
          left: `${startPosition}%`,
          width: `${Math.max(0, width)}%`
        };
      } else {
        // Negativo: rojo hacia la izquierda desde price_initial
        const width = startPosition - suggestionPosition;
        return {
          left: `${suggestionPosition}%`,
          width: `${Math.max(0, width)}%`
        };
      }
    }
  }
};
</script>

<style scoped lang="scss">
// ==========================================================================
// Variables
// ==========================================================================
$size: 8px;
$grey: #c7c7c7;
$grey-light: #e7e7e7;
$secondary: #1576c7;
$secondary-light: #aac2de;
$green: #1b998b;
$red: #e64d3c;
$radius: 4px;

// ==========================================================================
// Componente principal
// ==========================================================================
.a-pricing-strategy-recommendation-tooltip {
  // Layout principal
  .suggestion-row {
    width: 100%;
    // La regla ha saltado, pero otra le ha pasado por encima: se deja ver
    // porque forma parte de la explicación, apagada porque no suma.
    &.discarded {
      opacity: 0.45;
      .font-weight-bold.text-caption {
        text-decoration: line-through;
      }
    }
    &:last-child {
      margin-bottom: 0;
    }
    .chip {
      border-radius: 50%;
      padding: 5px;
      height: 24px;
      width: 25px;
    }
    // Label izquierdo con información
    .suggestion-label {
      min-width: 250px;
      max-width: 250px;
      padding-right: 20px;
      gap: 5px;
      .text-caption {
        line-height: 1.1 !important;
      }
    }
    // Contenedor de la barra
    .suggestion-bar-container {
      flex: 1;
      min-width: 0; // Permite que flex funcione correctamente
      // Contenedor interno
      .bar-container {
        width: 100%;
        position: relative;
        // Barra visual
        .bar-visual {
          height: $size;
          border-radius: $radius;
          position: relative;
          width: 100%;
          // Márgenes grises laterales (10% cada lado)
          &.with-margins,
          &.individual-bar {
            &::before,
            &::after {
              content: "";
              position: absolute;
              top: 0;
              height: 100%;
              background-color: $grey-light;
              z-index: 15;
            }
            &::before {
              left: 0;
              width: var(--side-margin, 10%);
              border-top-left-radius: $radius;
              border-bottom-left-radius: $radius;
            }
            &::after {
              right: 0;
              width: var(--side-margin, 10%);
              border-top-right-radius: $radius;
              border-bottom-right-radius: $radius;
            }
          }
          // Barra base gris
          .bar-base {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: $grey-light;
            border-radius: $radius;
          }
          // Barra de color (verde/roja)
          .bar-color {
            position: absolute;
            top: 0;
            height: 100%;
            border-radius: $radius;
            &::before {
              background-color: $grey-light;
              border-radius: 50%;
              content: "";
              position: absolute;
              display: block;
              height: $size;
              width: $size;
              z-index: 1;
            }
            &.positive {
              background-color: $green;
              &::before {
                margin-left: -1px;
                left: 0;
              }
            }
            &.negative {
              background-color: $red;
              &::before {
                margin-right: -1px;
                right: 0;
              }
            }
          }
          // Puntos de precio
          .current-price-point {
            position: absolute;
            top: 50%;
            transform: translate(-40%, -50%);
            width: 12px;
            height: 12px;
            border: 2px solid $secondary;
            background-color: white;
            border-radius: 50%;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
            z-index: 25;
            &.current-point {
              border-color: $secondary-light;
            }
          }
        }
        // Labels de precios debajo de la barra
        .price-labels {
          position: relative;
          height: 20px;
          margin-top: 4px;
          font-size: 11px;
          color: $grey;
          font-weight: 700;
          .price-label {
            position: absolute;
            top: 0;
            &.start-price {
              color: black;
            }
          }
        }
      }
      // Overlay para bloquear interacciones durante hover de tooltip
      .tooltip-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none; // Permite que los tooltips sigan siendo interactivos
        z-index: 40;
      }

      // Tooltips fijos (solo en primera barra)
      .fixed-tooltips {
        position: relative;
        margin-bottom: 10px;
        height: 30px;
        width: 100%;
        .fixed-tooltip {
          cursor: default;
          position: absolute;
          top: 0;
          z-index: 30; // Z-index base para tooltips
          &.current-tooltip {
            .tooltip-content {
              background-color: $secondary-light;
            }
            .tooltip-arrow {
              border-top-color: $secondary-light;
            }
          }
          .tooltip-content {
            background-color: $secondary;
            color: white;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 11px;
            white-space: nowrap;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          }
          .tooltip-arrow {
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 6px solid transparent;
            border-right: 6px solid transparent;
            border-top: 6px solid $secondary;
          }
        }
      }
    }
  }
}

// ==========================================================================
// Clases de utilidad globales
// ==========================================================================
.positive {
  color: $green;
}
.negative {
  color: $red;
}

// Elementos clickeables para reglas
.clickable-rule-info {
  pointer-events: auto !important;
  z-index: 100 !important;
  position: relative !important;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    transform: scale(0.98);
  }
}
</style>
